/**
 * Twenty Mae — Quick Add
 * Opens a modal with a swipeable product gallery + variant selectors + Add to Bag.
 * Uses /products/{handle}.js for product data.
 * Uses /cart/add.js?sections=... to add and refresh the cart drawer.
 */
(function () {
  'use strict';

  let currentProduct = null;
  let selectedVariantId = null;
  let colorOptionIndex = -1;
  let sizeOptionIndex = -1;
  let _backdropHandler = null;
  let _totalSlides = 0;
  let _currentSlide = 0;

  function getDialog() { return document.getElementById('tm-qa-dialog'); }

  // ── Money formatting ──────────────────────────────────────────
  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }
    return '€' + (cents / 100).toFixed(2);
  }

  // ── Shopify CDN image sizing ──────────────────────────────────
  function sizedImg(src, size) {
    if (!src) return '';
    return src.replace(/\.(jpg|jpeg|png|webp|gif|avif)(\?|$)/i, '_' + size + '.$1$2');
  }

  // ── Safe option name (handles string or object) ───────────────
  function optionName(o) {
    return (typeof o === 'string' ? o : (o && o.name) || '').toLowerCase();
  }

  // ── Safe variant option value — tries .options[] then option1/2/3 ──
  function variantOpt(v, idx) {
    if (Array.isArray(v.options) && v.options[idx] !== undefined) {
      return v.options[idx];
    }
    return v['option' + (idx + 1)] || '';
  }

  // ── Gallery ───────────────────────────────────────────────────
  function buildGallery(images) {
    const dialog = getDialog();
    const slidesEl = dialog.querySelector('.tm-qa-slides');
    const dotsEl = dialog.querySelector('.tm-qa-dots');

    slidesEl.innerHTML = '';
    dotsEl.innerHTML = '';
    // Remove any previous scroll listener before reassigning
    slidesEl.removeEventListener('scroll', onSlidesScroll);
    _totalSlides = 0;
    _currentSlide = 0;

    const imgs = Array.isArray(images) ? images.filter(Boolean) : [];
    if (!imgs.length) return;

    _totalSlides = imgs.length;

    imgs.forEach(function (src, i) {
      const srcStr = typeof src === 'string' ? src : (src && src.src) || '';
      if (!srcStr) return;

      const slide = document.createElement('div');
      slide.className = 'tm-qa-slide';

      const img = document.createElement('img');
      img.src = sizedImg(srcStr, '600x') || srcStr;
      img.alt = (currentProduct ? currentProduct.title : '') + (imgs.length > 1 ? ' — image ' + (i + 1) : '');
      img.loading = i < 2 ? 'eager' : 'lazy';
      img.decoding = 'async';

      slide.appendChild(img);
      slidesEl.appendChild(slide);

      // Dot per slide (only when >1 image)
      if (imgs.length > 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'tm-qa-dot' + (i === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', 'Image ' + (i + 1));
        dot.dataset.idx = i;
        dotsEl.appendChild(dot);
      }
    });

    // Dot click → jump to slide
    dotsEl.addEventListener('click', function (e) {
      const dot = e.target.closest('.tm-qa-dot');
      if (dot) goToSlide(parseInt(dot.dataset.idx, 10), false);
    });

    // Scroll → sync dots
    slidesEl.addEventListener('scroll', onSlidesScroll, { passive: true });
  }

  function goToSlide(idx, instant) {
    const dialog = getDialog();
    const slidesEl = dialog.querySelector('.tm-qa-slides');
    if (!slidesEl || _totalSlides === 0) return;

    const n = Math.max(0, Math.min(idx, _totalSlides - 1));
    const slideWidth = slidesEl.clientWidth;

    if (instant) {
      // Bypass smooth scroll for programmatic instant jumps
      const prev = slidesEl.style.scrollBehavior;
      slidesEl.style.scrollBehavior = 'auto';
      slidesEl.scrollLeft = n * slideWidth;
      // Restore after paint so future user swipes stay smooth
      requestAnimationFrame(function () {
        slidesEl.style.scrollBehavior = '';
      });
    } else {
      slidesEl.scrollLeft = n * slideWidth;
    }

    _currentSlide = n;
    updateDots(n);
  }

  function onSlidesScroll() {
    const dialog = getDialog();
    const slidesEl = dialog.querySelector('.tm-qa-slides');
    if (!slidesEl || !slidesEl.clientWidth) return;
    const idx = Math.round(slidesEl.scrollLeft / slidesEl.clientWidth);
    if (idx !== _currentSlide) {
      _currentSlide = idx;
      updateDots(idx);
    }
  }

  function updateDots(idx) {
    const dialog = getDialog();
    dialog.querySelectorAll('.tm-qa-dot').forEach(function (d, i) {
      d.classList.toggle('is-active', i === idx);
    });
  }

  // ── Open ──────────────────────────────────────────────────────
  function openFor(btn) {
    const handle = btn.dataset.tmQuickAdd;
    const initialColor = btn.dataset.tmQuickColor || null;
    if (!handle) return;

    const dialog = getDialog();
    const product = dialog.querySelector('.tm-qa-product');
    const slidesEl = dialog.querySelector('.tm-qa-slides');
    const dotsEl = dialog.querySelector('.tm-qa-dots');

    // Reset
    currentProduct = null;
    selectedVariantId = null;
    _totalSlides = 0;
    _currentSlide = 0;
    if (slidesEl) { slidesEl.removeEventListener('scroll', onSlidesScroll); slidesEl.innerHTML = ''; }
    if (dotsEl) dotsEl.innerHTML = '';
    dialog.querySelector('.tm-qa-title').textContent = '';
    dialog.querySelector('.tm-qa-price').innerHTML = '';
    dialog.querySelector('.tm-qa-colors').hidden = true;
    dialog.querySelector('.tm-qa-colors').innerHTML = '';
    dialog.querySelector('.tm-qa-sizes').hidden = true;
    dialog.querySelector('.tm-qa-sizes').innerHTML = '';
    dialog.querySelector('.tm-qa-hint').textContent = '';
    dialog.querySelector('.tm-qa-atc').disabled = true;
    product.dataset.loading = '';

    dialog.showModal();

    // Defer backdrop handler so the opening click doesn't immediately close
    if (_backdropHandler) dialog.removeEventListener('click', _backdropHandler);
    setTimeout(function () {
      _backdropHandler = function (e) { if (e.target === dialog) dialog.close(); };
      dialog.addEventListener('click', _backdropHandler);
    }, 0);

    fetch('/products/' + handle + '.js')
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (p) {
        delete product.dataset.loading;
        populate(p, initialColor);
      })
      .catch(function (err) {
        delete product.dataset.loading;
        dialog.querySelector('.tm-qa-hint').textContent = 'Could not load product. Please try again.';
        console.error('[TM Quick Add]', handle, err);
      });
  }

  // ── Populate modal with product data ─────────────────────────
  function populate(p, initialColor) {
    const dialog = getDialog();
    currentProduct = p;

    dialog.querySelector('.tm-qa-title').textContent = p.title;

    // Detect option positions
    const opts = Array.isArray(p.options) ? p.options : [];
    colorOptionIndex = opts.findIndex(function (o) {
      const n = optionName(o);
      return n === 'color' || n === 'colour';
    });
    sizeOptionIndex = opts.findIndex(function (o) {
      return optionName(o) === 'size';
    });

    const first = (p.variants && p.variants[0]) || null;
    setPrice(first);

    // Build gallery from all product images
    buildGallery(p.images || []);

    if (colorOptionIndex > -1) {
      const colors = [];
      const seen = {};
      (p.variants || []).forEach(function (v) {
        const c = variantOpt(v, colorOptionIndex);
        if (c && !seen[c]) { seen[c] = true; colors.push(c); }
      });
      if (colors.length) {
        buildColors(colors, initialColor);
      } else {
        buildSizes(p.variants || [], null);
      }
    } else {
      buildSizes(p.variants || [], null);
    }
  }

  // ── Color row ─────────────────────────────────────────────────
  function buildColors(colors, initialColor) {
    const dialog = getDialog();
    const wrap = dialog.querySelector('.tm-qa-colors');
    wrap.innerHTML = '<p class="tm-qa-option-label">Colour</p><div class="tm-qa-color-btns"></div>';
    const btnWrap = wrap.querySelector('.tm-qa-color-btns');

    colors.forEach(function (c) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'tm-qa-color-btn';
      b.dataset.color = c;
      b.textContent = c;
      btnWrap.appendChild(b);
    });

    wrap.hidden = false;

    btnWrap.addEventListener('click', function (e) {
      const b = e.target.closest('.tm-qa-color-btn');
      if (b) selectColor(b);
    });

    const target = initialColor
      ? (btnWrap.querySelector('[data-color="' + initialColor + '"]') || btnWrap.firstElementChild)
      : btnWrap.firstElementChild;
    if (target) selectColor(target);
  }

  function selectColor(btn) {
    const dialog = getDialog();
    dialog.querySelectorAll('.tm-qa-color-btn').forEach(function (b) {
      b.classList.toggle('is-active', b === btn);
    });

    const color = btn.dataset.color;
    const variantsForColor = currentProduct.variants.filter(function (v) {
      return variantOpt(v, colorOptionIndex) === color;
    });

    // Scroll gallery to this color's first image
    if (variantsForColor.length) {
      const v = variantsForColor[0];
      if (v.featured_image && v.featured_image.position) {
        goToSlide(v.featured_image.position - 1, false);
      }
      setPrice(v);
    }

    buildSizes(variantsForColor, color);
  }

  // ── Size row ──────────────────────────────────────────────────
  function buildSizes(variants, activeColor) {
    const dialog = getDialog();
    const wrap = dialog.querySelector('.tm-qa-sizes');

    selectedVariantId = null;
    dialog.querySelector('.tm-qa-atc').disabled = true;
    dialog.querySelector('.tm-qa-hint').textContent = '';

    if (sizeOptionIndex === -1) {
      if (variants.length === 1) {
        selectedVariantId = variants[0].id;
        setPrice(variants[0]);
        dialog.querySelector('.tm-qa-atc').disabled = !variants[0].available;
        if (!variants[0].available) {
          dialog.querySelector('.tm-qa-hint').textContent = 'Sold out';
        }
      }
      wrap.hidden = true;
      return;
    }

    const sizeMap = new Map();
    variants.forEach(function (v) {
      const sz = variantOpt(v, sizeOptionIndex);
      if (sz && !sizeMap.has(sz)) sizeMap.set(sz, v);
    });

    wrap.innerHTML = '<p class="tm-qa-option-label">Size</p><div class="tm-qa-size-btns"></div>';
    const btnWrap = wrap.querySelector('.tm-qa-size-btns');

    sizeMap.forEach(function (variant, size) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'tm-qa-size-btn' + (variant.available ? '' : ' is-sold-out');
      b.dataset.variantId = variant.id;
      b.textContent = size;
      if (!variant.available) b.setAttribute('aria-label', size + ' — Sold out');
      btnWrap.appendChild(b);
    });

    wrap.hidden = false;

    btnWrap.addEventListener('click', function (e) {
      const b = e.target.closest('.tm-qa-size-btn');
      if (b && !b.classList.contains('is-sold-out')) selectSize(b);
    });
  }

  function selectSize(btn) {
    const dialog = getDialog();
    dialog.querySelectorAll('.tm-qa-size-btn').forEach(function (b) {
      b.classList.toggle('is-active', b === btn);
    });

    const variantId = parseInt(btn.dataset.variantId, 10);
    const variant = currentProduct.variants.find(function (v) { return v.id === variantId; });
    if (!variant) return;

    selectedVariantId = variantId;
    setPrice(variant);
    dialog.querySelector('.tm-qa-atc').disabled = !variant.available;
    dialog.querySelector('.tm-qa-hint').textContent = variant.available ? '' : 'Sold out';
  }

  // ── Price ─────────────────────────────────────────────────────
  function setPrice(variant) {
    const dialog = getDialog();
    const el = dialog.querySelector('.tm-qa-price');
    if (!variant) { el.textContent = ''; return; }
    const sale = variant.compare_at_price && variant.compare_at_price > variant.price;
    el.innerHTML = sale
      ? '<span class="tm-qa-sale-price">' + formatMoney(variant.price) + '</span>'
        + '<s class="tm-qa-compare-price">' + formatMoney(variant.compare_at_price) + '</s>'
      : formatMoney(variant.price);
  }

  // ── Add to cart ───────────────────────────────────────────────
  function addToCart() {
    if (!selectedVariantId) return;

    const dialog = getDialog();
    const atcBtn = dialog.querySelector('.tm-qa-atc');
    const hintEl = dialog.querySelector('.tm-qa-hint');

    atcBtn.classList.add('is-loading');
    atcBtn.disabled = true;
    hintEl.textContent = '';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: selectedVariantId,
        quantity: 1,
        sections: ['cart-drawer', 'cart-icon-bubble']
      })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (d) { throw new Error(d.description || 'Error'); });
        return r.json();
      })
      .then(function (response) {
        dialog.close();
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && response.sections && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(response);
        } else if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        } else {
          const cartBtn = document.querySelector('#cart-icon-bubble');
          if (cartBtn) cartBtn.click();
        }
      })
      .catch(function (err) {
        atcBtn.classList.remove('is-loading');
        atcBtn.disabled = false;
        hintEl.textContent = err.message || 'Could not add to bag. Please try again.';
      });
  }

  // ── Init ──────────────────────────────────────────────────────
  function init() {
    const dialog = getDialog();
    if (!dialog) return;

    dialog.addEventListener('close', function () {
      if (_backdropHandler) {
        dialog.removeEventListener('click', _backdropHandler);
        _backdropHandler = null;
      }
    });

    // Clicks inside the modal don't reach the backdrop
    dialog.querySelector('.tm-qa-inner').addEventListener('click', function (e) {
      e.stopPropagation();
    });

    dialog.querySelector('.tm-qa-close').addEventListener('click', function () {
      dialog.close();
    });

    dialog.querySelector('.tm-qa-atc').addEventListener('click', addToCart);

    // Gallery prev/next arrows
    const prevBtn = dialog.querySelector('.tm-qa-gallery-btn--prev');
    const nextBtn = dialog.querySelector('.tm-qa-gallery-btn--next');
    if (prevBtn) prevBtn.addEventListener('click', function (e) { e.stopPropagation(); goToSlide(_currentSlide - 1, false); });
    if (nextBtn) nextBtn.addEventListener('click', function (e) { e.stopPropagation(); goToSlide(_currentSlide + 1, false); });

    // Open on card click — capture phase so we beat link navigation
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-tm-quick-add]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      openFor(btn);
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
